import { useLang } from "@/contexts/LangContext";
import { navx } from "@/i18n/navLabels";
import { Mail, MapPin, Instagram, Linkedin, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";
import logo from "@/assets/logo.png";

const WHATSAPP_NUMBER = "971589656886";
const WHATSAPP_DISPLAY = "+971 58 965 6886";
const INSTAGRAM_URL = "https://www.instagram.com/the_china_explorer/";
const LINKEDIN_URL = "https://www.linkedin.com/company/the-china-explorer/";
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}`;

const FooterCol = ({ title, links, testId }) => (
  <div data-testid={testId}>
    <p className="font-heading text-lg text-white mb-4">{title}</p>
    <ul className="space-y-2.5 text-sm">
      {links.map((l) => (
        <li key={l.label}>
          <Link to={l.to} data-testid={l.testId} className="text-white/70 hover:text-[#1FA3E0] transition-colors">
            {l.label}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

const Footer = () => {
  const { t, lang } = useLang();
  const nx = navx(lang);
  return (
    <footer
      data-testid="site-footer"
      className="bg-[#0E2A5C] text-white/75 pt-16 pb-10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-3 lg:grid-cols-7 gap-10 pb-12 border-b border-white/10">
          <div className="lg:col-span-2">
            <span className="inline-flex bg-white px-3 py-2 rounded-md">
              <img
                src={logo}
                alt="The China Explorer"
                className="h-12 w-auto"
              />
            </span>
            <p className="mt-5 text-sm leading-relaxed text-white/65 max-w-sm">
              {t.footer.tagline}
            </p>
            <p className="mt-3 text-xs uppercase tracking-[0.2em] text-[#D4AF37]" data-testid="footer-brand-line">
              China is our specialty. The world is within reach.
            </p>
            <ul className="mt-6 space-y-3 text-sm">
              <li>
                <a
                  data-testid="footer-email"
                  href="mailto:hello@thechinaexplorer.com"
                  className="flex items-center gap-3 hover:text-[#1FA3E0] transition-colors"
                >
                  <Mail size={14} className="text-[#1FA3E0]" />
                  hello@thechinaexplorer.com
                </a>
              </li>
              <li>
                <a
                  data-testid="footer-whatsapp"
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 hover:text-[#1FA3E0] transition-colors"
                >
                  <MessageCircle size={14} className="text-[#25D366]" />
                  {WHATSAPP_DISPLAY}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={14} className="text-[#1FA3E0] mt-1 flex-shrink-0" />
                <span>
                  <span className="block text-white">Dubai, UAE — serving customers worldwide</span>
                  <span className="block text-white/55 text-xs mt-0.5">
                    Operations · Guangzhou · Beijing · Shanghai
                  </span>
                </span>
              </li>
            </ul>
            <div className="flex gap-3 mt-6">
              <a data-testid="social-instagram" href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" aria-label="Instagram"
                className="w-11 h-11 border border-white/20 hover:border-[#1FA3E0] hover:text-[#1FA3E0] hover:bg-white/5 flex items-center justify-center transition-all duration-300">
                <Instagram size={17} />
              </a>
              <a data-testid="social-linkedin" href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"
                className="w-11 h-11 border border-white/20 hover:border-[#1FA3E0] hover:text-[#1FA3E0] hover:bg-white/5 flex items-center justify-center transition-all duration-300">
                <Linkedin size={17} />
              </a>
              <a data-testid="social-whatsapp" href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp"
                className="w-11 h-11 border border-white/20 hover:border-[#25D366] hover:text-[#25D366] hover:bg-white/5 flex items-center justify-center transition-all duration-300">
                <MessageCircle size={17} />
              </a>
            </div>
          </div>

          <FooterCol title={nx.company} testId="footer-col-company" links={[
            { label: t.nav.whyUs, to: "/#why-us" },
            { label: t.nav.contact, to: "/#contact" },
            { label: nx.blog, to: "/blog" },
          ]} />

          <FooterCol title={nx.exploreChina} testId="footer-col-explore" links={[
            { label: t.nav.destinations, to: "/#destinations" },
            { label: nx.educationTrips, to: "/#education" },
            { label: nx.medicalTrips, to: "/#services" },
            { label: nx.chinaExperiences, to: "/#experiences" },
          ]} />

          <FooterCol title={nx.travelSupport} testId="footer-col-support" links={[
            { label: nx.visaAssistance, to: "/#visa" },
            { label: nx.planMyTrip, to: "/#itinerary" },
            { label: t.nav.faq, to: "/#faq" },
          ]} />

          <div data-testid="footer-col-book">
            <p className="font-heading text-lg text-white mb-4">{nx.bookTravel}</p>
            <ul className="space-y-2.5 text-sm">
              <li>
                <Link data-testid="footer-link-flights" to="/flights" className="inline-flex items-center gap-2 text-white font-semibold hover:text-[#D4AF37] transition-colors">
                  ✈ {nx.globalFlights}
                </Link>
              </li>
              <li className="text-white/50">
                🏨 {nx.globalHotels} <span className="text-[9px] uppercase tracking-[0.15em] font-bold text-[#D4AF37]">{nx.comingSoon}</span>
              </li>
              <li><Link to="/flights#more-ways" className="text-white/70 hover:text-[#1FA3E0] transition-colors">🚆 {nx.rail}</Link></li>
              <li><Link to="/flights#more-ways" className="text-white/70 hover:text-[#1FA3E0] transition-colors">🚌 {nx.bus}</Link></li>
            </ul>
          </div>

          <FooterCol title={nx.business} testId="footer-col-business" links={[
            { label: nx.businessTravel, to: "/china-business-explorer", testId: "footer-link-business" },
            { label: nx.b2bLogin, to: "/login", testId: "footer-link-b2b-login" },
          ]} />
        </div>
        <p className="pt-8 text-xs uppercase tracking-[0.2em] text-white/50">
          {t.footer.rights}
        </p>
      </div>

      {/* Floating WhatsApp quick-contact button — sits above AI chat toggle on the right */}
      <a
        data-testid="floating-whatsapp"
        href={WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="WhatsApp Chat"
        className="fixed bottom-44 right-7 z-40 w-14 h-14 rounded-full bg-[#25D366] hover:bg-[#1da851] text-white shadow-xl shadow-black/25 flex items-center justify-center transition-all duration-300 hover:scale-110"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          className="w-7 h-7"
        >
          <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.516 5.26l-.999 3.648 3.972-1.607zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
        </svg>
      </a>
    </footer>
  );
};

export default Footer;
